/**
 * Drive Comfort premium layer. No dependencies.
 * Cart updates reuse the theme's own CartAddEvent so the cart drawer,
 * cart icon count and cart page all refresh through the existing code paths.
 */
import { CartAddEvent } from '@theme/events';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const root = () => window.Shopify?.routes?.root || '/';

/* Slider: scroll-snap track with arrows, dots and optional autoplay */
class DcSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.slides = () => [...this.track.querySelectorAll(':scope > [data-slide]')];
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.pauseBtn = this.querySelector('[data-pause]');

    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.dot))));
    this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
    window.addEventListener('resize', () => this.queueUpdate(), { passive: true });
    this.update();

    // Off-screen slide images are deferred (data-dc-srcset) so the first slide gets the bandwidth.
    if (this.querySelector('[data-dc-defer]')) {
      const later = () => (window.requestIdleCallback || setTimeout)(() => this.loadDeferred());
      if (document.readyState === 'complete') later();
      else window.addEventListener('load', later, { once: true });
      ['pointerdown', 'focusin'].forEach((e) => this.addEventListener(e, () => this.loadDeferred(), { once: true, passive: true }));
      this.track.addEventListener('scroll', () => this.loadDeferred(), { once: true, passive: true });
    }

    const delay = Number(this.dataset.autoplay || 0);
    if (delay && !reduceMotion.matches) {
      this.delay = delay;
      this.userPaused = false;
      this.pauseBtn?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.pauseBtn.setAttribute('aria-pressed', String(this.userPaused));
        this.pauseBtn.setAttribute('aria-label', this.userPaused ? 'Play slideshow' : 'Pause slideshow');
        this.pauseBtn.classList.toggle('is-paused', this.userPaused);
        this.userPaused ? this.stop() : this.play();
      });
      ['mouseenter', 'focusin', 'touchstart'].forEach((e) => this.addEventListener(e, () => this.stop(), { passive: true }));
      ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => !this.userPaused && this.play()));
      document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : !this.userPaused && this.play()));
      this.play();
    } else {
      this.pauseBtn?.remove();
    }
  }

  disconnectedCallback() {
    this.stop();
  }

  queueUpdate() {
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = null;
      this.update();
    });
  }

  get index() {
    const left = this.track.scrollLeft;
    let best = 0;
    let bestDist = Infinity;
    this.slides().forEach((s, i) => {
      const d = Math.abs(s.offsetLeft - this.track.offsetLeft - left);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    return best;
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const overflow = scrollWidth - clientWidth > 4;
    this.classList.toggle('has-overflow', overflow);
    if (this.prev) this.prev.disabled = !overflow || scrollLeft <= 2;
    if (this.next) this.next.disabled = !overflow || scrollLeft >= scrollWidth - clientWidth - 2;
    if (this.dots.length) {
      const i = this.index;
      this.dots.forEach((d, n) => (n === i ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current')));
      this.slides().forEach((s, n) => s.toggleAttribute('inert', n !== i));
      if (this.prev) this.prev.disabled = false;
      if (this.next) this.next.disabled = false;
    }
  }

  loadDeferred() {
    this.querySelectorAll('[data-dc-srcset]').forEach((el) => {
      el.srcset = el.dataset.dcSrcset;
      el.removeAttribute('data-dc-srcset');
    });
    this.querySelectorAll('[data-dc-src]').forEach((el) => {
      el.src = el.dataset.dcSrc;
      el.removeAttribute('data-dc-src');
    });
  }

  goTo(i) {
    this.loadDeferred();
    const slides = this.slides();
    const target = slides[(i + slides.length) % slides.length];
    if (!target) return;
    this.track.scrollTo({ left: target.offsetLeft - this.track.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }

  step(dir) {
    if (this.dots.length) return this.goTo(this.index + dir);
    const amount = Math.max(this.track.clientWidth * 0.85, 200) * dir;
    this.track.scrollBy({ left: amount, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }

  play() {
    this.stop();
    this.timer = setInterval(() => {
      const last = this.index >= this.slides().length - 1;
      this.goTo(last ? 0 : this.index + 1);
    }, this.delay);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }
}
if (!customElements.get('dc-slider')) customElements.define('dc-slider', DcSlider);

/* Vehicle finder: Make -> Model -> Accessory type */
class DcVehicleFinder extends HTMLElement {
  connectedCallback() {
    try {
      this.data = JSON.parse(this.querySelector('[data-finder-json]')?.textContent || '[]');
    } catch (e) {
      this.data = [];
    }
    this.make = this.querySelector('[data-select-make]');
    this.model = this.querySelector('[data-select-model]');
    this.cat = this.querySelector('[data-select-category]');
    this.submit = this.querySelector('[data-finder-submit]');
    this.storageKey = this.dataset.storageKey;

    this.make?.addEventListener('change', () => this.onMake());
    this.model?.addEventListener('change', () => this.onModel());
    this.cat?.addEventListener('change', () => this.sync());
    this.querySelector('[data-finder-form]')?.addEventListener('submit', (e) => this.go(e));

    this.querySelectorAll('[data-make-tab]').forEach((tab) => {
      tab.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        this.showMake(tab.dataset.makeTab, true);
      });
      tab.addEventListener('keydown', (e) => this.tabKeys(e));
    });
    this.querySelector('[data-saved-car-clear]')?.addEventListener('click', () => {
      this.store(null);
      this.renderSaved();
      this.make?.focus();
    });
    this.renderSaved();
  }

  findMake(i) {
    return this.data.find((m) => String(m.i) === String(i));
  }

  fill(select, placeholder, items) {
    select.innerHTML = '';
    select.append(new Option(placeholder, ''));
    items.forEach((item, idx) => select.append(new Option(item, String(idx))));
  }

  onMake() {
    const make = this.findMake(this.make.value);
    const models = make ? make.models : [];
    this.fill(this.model, models.length ? 'Select model' : 'No models listed', models.map((m) => `${m.name} (${m.count})`));
    this.model.disabled = !models.length;
    this.fill(this.cat, 'All accessories', []);
    this.cat.disabled = true;
    if (make) this.showMake(make.i, false);
    this.sync();
  }

  onModel() {
    const model = this.currentModel();
    const cats = model ? model.cats : [];
    this.fill(this.cat, 'All accessories', cats.map((c) => `${c.label} (${c.count})`));
    this.cat.disabled = !cats.length;
    this.sync();
  }

  currentModel() {
    const make = this.findMake(this.make.value);
    return make && this.model.value !== '' ? make.models[Number(this.model.value)] : null;
  }

  sync() {
    if (this.submit) this.submit.disabled = !this.make.value;
  }

  go(event) {
    event.preventDefault();
    const make = this.findMake(this.make.value);
    if (!make) return this.make.focus();
    const model = this.currentModel();
    let url = make.url;
    if (model) {
      url = model.url;
      const cat = this.cat.value !== '' ? model.cats[Number(this.cat.value)] : null;
      if (cat) url = cat.url;
      this.store({ name: `${make.name} ${model.name}`, url: model.url });
    }
    window.location.href = url;
  }

  showMake(i, focus) {
    this.querySelectorAll('[data-make-tab]').forEach((t) => t.setAttribute('aria-selected', String(t.dataset.makeTab === String(i))));
    this.querySelectorAll('[data-make-panel]').forEach((p) => p.classList.toggle('is-active', p.dataset.makePanel === String(i)));
    const active = this.querySelector(`[data-make-tab="${i}"]`);
    if (active) {
      const bar = active.parentElement;
      bar.scrollTo({ left: active.offsetLeft - bar.offsetLeft - 16, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      if (focus) active.focus({ preventScroll: true });
    }
  }

  tabKeys(e) {
    const tabs = [...this.querySelectorAll('[data-make-tab]')];
    const i = tabs.indexOf(e.currentTarget);
    const map = { ArrowRight: (i + 1) % tabs.length, ArrowLeft: (i - 1 + tabs.length) % tabs.length, Home: 0, End: tabs.length - 1 };
    if (!(e.key in map)) return;
    e.preventDefault();
    this.showMake(tabs[map[e.key]].dataset.makeTab, true);
  }

  store(car) {
    try {
      car ? localStorage.setItem(this.storageKey, JSON.stringify(car)) : localStorage.removeItem(this.storageKey);
    } catch (e) {
      /* storage unavailable: finder still works */
    }
  }

  renderSaved() {
    const box = this.querySelector('[data-saved-car]');
    if (!box) return;
    let car = null;
    try {
      car = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
    } catch (e) {
      car = null;
    }
    box.hidden = !car;
    if (car) {
      box.querySelector('[data-saved-car-name]').textContent = car.name;
      box.querySelector('[data-saved-car-link]').href = car.url;
    }
  }
}
if (!customElements.get('dc-vehicle-finder')) customElements.define('dc-vehicle-finder', DcVehicleFinder);

/* Read more (content stays in the DOM for SEO) */
class DcReadMore extends HTMLElement {
  connectedCallback() {
    const content = this.querySelector('[data-content]');
    const toggle = this.querySelector('[data-toggle]');
    if (!content || !toggle) return;
    // Long descriptions arrive clamped from Liquid; only un-clamp if the text actually fits.
    if (this.classList.contains('is-clamped')) {
      requestAnimationFrame(() => {
        if (content.scrollHeight - content.clientHeight <= 4) {
          this.classList.remove('is-clamped');
          toggle.hidden = true;
        }
      });
    }
    toggle.addEventListener('click', () => {
      const open = this.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
      toggle.textContent = open ? 'Read less' : 'Read more';
    });
  }
}
if (!customElements.get('dc-read-more')) customElements.define('dc-read-more', DcReadMore);

/* Quick add for single-variant products on dc-product-card */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-dc-quick-add]');
  if (!button || button.disabled) return;
  event.preventDefault();
  const label = button.querySelector('span');
  const original = label?.textContent;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
  try {
    const res = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(button.dataset.dcQuickAdd), quantity: 1 }], sections: sectionIds.join(',') }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
    const cart = await fetch(`${root()}cart.js`).then((r) => r.json());
    document.dispatchEvent(
      new CartAddEvent(cart, button.dataset.dcQuickAdd, {
        source: 'dc-quick-add',
        itemCount: cart.item_count,
        variantId: button.dataset.dcQuickAdd,
        sections: data.sections,
      })
    );
    if (label) label.textContent = 'Added';
    button.classList.add('is-added');
  } catch (error) {
    if (label) label.textContent = 'Try again';
    button.title = error.message;
    console.error(error);
  } finally {
    button.removeAttribute('aria-busy');
    setTimeout(() => {
      button.disabled = false;
      button.classList.remove('is-added');
      if (label && original) label.textContent = original;
    }, 1800);
  }
});

/* Recently viewed (ids recorded by the theme in localStorage) */
async function loadRecentlyViewed() {
  const host = document.querySelector('[data-dc-recent]');
  if (!host) return;
  let ids = [];
  try {
    ids = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
  } catch (e) {
    return;
  }
  ids = ids.filter((id) => id && id !== host.dataset.exclude);
  if (!ids.length) return;
  const url = new URL(`${root()}search`, location.origin);
  url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
  url.searchParams.set('type', 'product');
  url.searchParams.set('section_id', 'dc-recently-viewed');
  try {
    const html = await fetch(url).then((r) => (r.ok ? r.text() : ''));
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-dc-recent]');
    if (!fresh || !fresh.querySelector('[data-slide]')) return;
    const track = fresh.querySelector('[data-track]');
    const items = [...track.children];
    ids.forEach((id) => {
      const li = items.find((el) => el.dataset.productId === id);
      if (li) track.append(li);
    });
    host.innerHTML = fresh.innerHTML;
    host.hidden = false;
  } catch (e) {
    /* non-critical */
  }
}
if ('requestIdleCallback' in window) requestIdleCallback(loadRecentlyViewed, { timeout: 2500 });
else setTimeout(loadRecentlyViewed, 1200);

/* Footer columns collapse on mobile, stay open on larger screens */
const footerCols = document.querySelectorAll('[data-footer-col]');
const mobileQuery = window.matchMedia('(max-width: 749px)');
const syncFooter = () => footerCols.forEach((d) => (d.open = !mobileQuery.matches));
syncFooter();
mobileQuery.addEventListener('change', syncFooter);
